import { SpaceBetween, FormField, Input, RadioGroup, Link } from '@amzn/awsui-components-react';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { useState } from 'react';
import { EventInput } from '@amzn/red-velvet-api';
import { publishKatalMetric } from '../../../katalAnalytics';
import { isValidGrantIdFormat } from '../../../../utils/eventValidation';
import { FIELD_LIMITS } from '../../../../utils/fieldConstraints';

export interface AciGrantComponentProps {
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  errors?: Record<string, string>;
  isReadOnly?: boolean;
  onGrantInfoClick?: () => void;
  metricPrefix: string;
}

export function AciGrantComponent(props: AciGrantComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const { currentFields, updateFieldHandler, errors, isReadOnly, onGrantInfoClick, metricPrefix } =
    props;

  const [aciGrant, setAciGrant] = useState(currentFields.usedAciGrant);
  const [grantId, setGrantId] = useState(currentFields.grantId ?? '');
  const [grantIdValid, setGrantIdValid] = useState(() =>
    isValidGrantIdFormat(currentFields.grantId ?? '')
  );

  const renderGrantInfo = () => {
    if (aciGrant === true) {
      return (
        <FormField
          label={t('grant.id', { ns: 'translation' })}
          description={t('grant.idSubText', { ns: 'translation' })}
          {...(!grantIdValid ? { warningText: t('grant.idWarning', { ns: 'translation' }) } : {})}
          errorText={
            errors?.grantId ??
            (grantId?.length > FIELD_LIMITS.GRANT_ID
              ? t('error.inputTooLong', { maxLength: FIELD_LIMITS.GRANT_ID })
              : '')
          }
        >
          <Input
            placeholder={t('grant.idPlaceholder', { ns: 'translation' })}
            onChange={(event) => {
              const providedGrantId = event.detail.value;
              const isValid = isValidGrantIdFormat(providedGrantId);
              setGrantIdValid(isValid);
              setGrantId(providedGrantId);
            }}
            onBlur={() => {
              updateFieldHandler({
                ...currentFields,
                grantId: grantId,
              });
            }}
            value={grantId}
            readOnly={isReadOnly}
          />
        </FormField>
      );
    }
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <FormField
        label={t('grant.question', { ns: 'translation' })}
        description={t('grant.questionSubText', { ns: 'translation' })}
        errorText={errors?.usedAciGrant}
        info={
          onGrantInfoClick && (
            <Link
              variant="info"
              data-aci-analytics-name="eventCreation_grantInfoOpened"
              onFollow={(e) => {
                e.preventDefault();
                onGrantInfoClick();
              }}
            >
              {t('misc.info')}
            </Link>
          )
        }
      >
        <RadioGroup
          onChange={(event) => {
            publishKatalMetric(`${metricPrefix}_grantUsageChanged`, event.detail.value);
            setAciGrant(event.detail.value === 'true');
            // Resetting the grant id value each time aci grant change
            setGrantId('');
            setGrantIdValid(true);
            updateFieldHandler({
              ...currentFields,
              usedAciGrant: event.detail.value === 'true',
              grantId: '',
            });
          }}
          value={aciGrant?.toString() ?? ''}
          items={[
            { value: 'true', label: t('misc.yes') },
            { value: 'false', label: t('misc.no') },
          ]}
          readOnly={isReadOnly}
        />
      </FormField>
      {renderGrantInfo()}
    </SpaceBetween>
  );
}
